import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAgentAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  History,
  LogOut,
  KeyRound,
  Wallet,
  Bitcoin,
  Banknote,
  RefreshCw,
  ChevronRight,
  Shield,
  Sparkles,
  Menu,
  X,
  Smartphone,
  Download,
  BookOpen,
  FileText,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import NotificationBell from "./NotificationBell";
import api from "../lib/api";

const items = [
  { to: "/agent/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "agent-nav-dashboard", group: "Overview" },
  { to: "/agent/deposit", label: "Deposit to User", icon: ArrowDownToLine, testid: "agent-nav-deposit", group: "Operations" },
  { to: "/agent/usdt-deposit", label: "USDT Deposit", icon: Bitcoin, testid: "agent-nav-usdt-deposit", group: "Operations" },
  { to: "/agent/bdt-deposit", label: "BDT Deposit", icon: Banknote, testid: "agent-nav-bdt-deposit", group: "Operations" },
  { to: "/agent/withdrawals", label: "Pending Withdrawals", icon: ArrowUpFromLine, testid: "agent-nav-withdrawals", group: "Operations" },
  { to: "/agent/history", label: "History", icon: History, testid: "agent-nav-history", group: "Records" },
  { to: "/agent/my-statements", label: "My Statements", icon: FileText, testid: "agent-nav-my-statements", group: "Records" },
  { to: "/agent/change-password", label: "Change Password", icon: KeyRound, testid: "agent-nav-change-password", group: "Account" },
  { to: "/agent/two-factor", label: "Two-Factor Auth", icon: Shield, testid: "agent-nav-two-factor", group: "Account" },
];

const greet = (h) => (h < 5 ? "Good night" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : h < 21 ? "Good evening" : "Good night");

export default function AgentLayout() {
  const { agent, logout, refresh } = useAgentAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [now, setNow] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [apkInfo, setApkInfo] = useState(null);
  const [apkAgentCfg, setApkAgentCfg] = useState({ apk_agent_url: "", apk_agent_enabled: true });
  const [guidelineMeta, setGuidelineMeta] = useState(null);
  const [downloadingGuide, setDownloadingGuide] = useState(false);

  useEffect(() => {
    // Fetch APK metadata once
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/agent/app/info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && d.available && setApkInfo(d))
      .catch(() => null);
    // Fetch admin-configurable APK link for agents (overrides /api/agent/app/download)
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/public/site-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setApkAgentCfg({
          apk_agent_url: (d.apk_agent_url || "").trim(),
          apk_agent_enabled: d.apk_agent_enabled !== false,
        });
      })
      .catch(() => null);
    // Guideline metadata (logged-in agent only)
    api.get("/agent/guideline/info")
      .then(({ data }) => setGuidelineMeta(data))
      .catch(() => null);
  }, []);

  // Inject PWA manifest + handle install prompt for agent panel
  useEffect(() => {
    // Add agent manifest link
    let link = document.querySelector('link[rel="manifest"]#agent-manifest');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      link.id = "agent-manifest";
      link.href = "/agent-manifest.json";
      document.head.appendChild(link);
    }
    // Theme color
    let meta = document.querySelector('meta[name="theme-color"]#agent-theme');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      meta.id = "agent-theme";
      meta.content = "#070d18";
      document.head.appendChild(meta);
    }

    // Service worker — intentionally NOT registering one anymore. The agent
    // panel previously registered `/agent-sw.js` which caused stale cached
    // responses to replay after hard-refresh. We deploy a kill-switch SW at
    // `/agent-sw.js` (see public/agent-sw.js) so any browser that still has
    // the old SW will fetch the new one and unregister itself.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then((regs) => regs.forEach((r) => {
          if (r.scope && r.scope.includes("/agent")) r.unregister().catch(() => null);
        }))
        .catch(() => null);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // Check if already running as standalone PWA
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/agent/login");
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refresh();
    setTimeout(() => setRefreshing(false), 600);
  };

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === "accepted") toast.success("App installed!");
      setInstallPrompt(null);
    } else {
      toast.info("Use your browser menu → \"Add to Home screen\" to install.");
    }
  };

  const handleGuidelineDownload = async () => {
    if (!guidelineMeta?.available) {
      toast.error("Guideline is not available yet. Please contact admin.");
      return;
    }
    setDownloadingGuide(true);
    try {
      const res = await api.get("/agent/guideline/download", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = guidelineMeta.filename || "Maxlive88_Agent_Guidelines_BN.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
      toast.success("Guideline PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to download guideline");
    } finally {
      setDownloadingGuide(false);
    }
  };



  const handleApkDownload = () => {
    if (apkAgentCfg.apk_agent_enabled === false) {
      toast.error("APK download is disabled by admin.");
      return;
    }
    // Prefer admin-configured URL (Admin Panel → Configuration → APK → Agent APK URL).
    // Fallback to backend-served APK at /api/agent/app/download.
    const configured = apkAgentCfg.apk_agent_url;
    let url;
    if (configured) {
      url = /^https?:\/\//i.test(configured)
        ? configured
        : `${process.env.REACT_APP_BACKEND_URL || ""}${configured.startsWith("/") ? "" : "/"}${configured}`;
    } else {
      url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/agent/app/download`;
    }
    window.location.href = url;
    toast.success("Downloading Agent App APK…");
  };

  const currentItem = items.find((i) => location.pathname.startsWith(i.to));
  const initials = (agent?.name || agent?.username || "A").slice(0, 2).toUpperCase();
  const grouped = items.reduce((acc, it) => {
    (acc[it.group] = acc[it.group] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="min-h-screen text-white flex relative overflow-hidden" data-testid="agent-layout"
      style={{
        background:
          "radial-gradient(1200px 600px at -10% -10%, rgba(16,185,129,0.10), transparent 60%)," +
          "radial-gradient(1000px 500px at 110% 10%, rgba(245,158,11,0.07), transparent 60%)," +
          "radial-gradient(900px 700px at 50% 120%, rgba(56,189,248,0.06), transparent 60%)," +
          "linear-gradient(180deg, #050912 0%, #070d18 100%)",
      }}
    >
      {/* subtle grid */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }} />

      {/* Mobile backdrop */}
      <div
        onClick={() => setDrawerOpen(false)}
        className={`lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40 transition-opacity ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!drawerOpen}
        data-testid="agent-drawer-backdrop"
      />

      {/* Sidebar (drawer on mobile, fixed on desktop) */}
      <aside
        className={[
          "fixed lg:sticky top-0 left-0 z-50 h-screen w-[280px] flex flex-col flex-shrink-0",
          "border-r border-white/[0.06]",
          "transition-transform duration-300",
          "lg:w-[260px] lg:translate-x-0",
          drawerOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
        style={{ background: "linear-gradient(180deg, rgba(8,12,22,0.96) 0%, rgba(7,11,18,0.96) 100%)", backdropFilter: "blur(14px)" }}
        data-testid="agent-sidebar"
      >
        {/* Brand row with mobile close */}
        <div className="px-5 pt-5 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="h-11 w-11 rounded-xl grid place-items-center font-black text-[#06140b] text-lg shadow-[0_10px_30px_-8px_rgba(16,185,129,0.55)]"
                style={{ background: "linear-gradient(135deg, #34d399 0%, #059669 100%)" }}>
                M
              </div>
              <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-amber-400 border-2 border-[#070d18] grid place-items-center">
                <Sparkles size={8} className="text-[#1a1208]" strokeWidth={3} />
              </span>
            </div>
            <div className="leading-tight">
              <div className="text-[15px] font-extrabold tracking-tight">Maxlive88</div>
              <div className="text-[10px] uppercase tracking-[2.3px] text-emerald-400/85 font-bold">Agent Panel</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden h-9 w-9 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/5 transition"
            aria-label="Close menu"
            data-testid="agent-drawer-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Agent profile card */}
        <div className="mx-3 mb-4 rounded-2xl p-3.5 border border-white/[0.08] relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(16,185,129,0.02) 60%, rgba(245,158,11,0.06) 100%)" }}>
          <div aria-hidden className="absolute -top-8 -right-8 h-20 w-20 rounded-full bg-emerald-400/15 blur-2xl" />
          <div className="relative flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl grid place-items-center font-black text-[13px] flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#1a1208", boxShadow: "0 8px 18px -8px rgba(245,158,11,0.55)" }}>
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-bold truncate" data-testid="agent-sidebar-name">{agent?.name || "Agent"}</div>
              <div className="text-[10.5px] text-white/55 truncate font-mono">@{agent?.username}</div>
            </div>
          </div>
          <div className="relative mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
            <div>
              <div className="text-[9px] uppercase tracking-[1.6px] text-emerald-300/80 font-bold">Balance</div>
              <div className="font-mono font-extrabold text-[15px] text-white tabular-nums" data-testid="agent-sidebar-balance">
                ৳{Number(agent?.balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className="h-8 w-8 grid place-items-center rounded-lg bg-white/5 hover:bg-emerald-500/15 border border-white/10 hover:border-emerald-500/40 text-white/60 hover:text-emerald-300 transition"
              data-testid="agent-sidebar-refresh"
              title="Refresh balance"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Nav grouped */}
        <nav className="flex-1 px-3 pb-3 overflow-y-auto custom-scroll">
          {Object.entries(grouped).map(([group, list]) => (
            <div key={group} className="mb-3">
              <div className="px-3 py-1.5 text-[9.5px] uppercase tracking-[2px] font-bold text-white/35">{group}</div>
              <div className="space-y-0.5">
                {list.map(({ to, label, icon: Icon, testid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={testid}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-[12.5px] font-medium transition-all ${
                        isActive
                          ? "text-white"
                          : "text-white/65 hover:text-white hover:bg-white/[0.04]"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span aria-hidden className="absolute inset-0 rounded-lg overflow-hidden">
                            <span className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-emerald-500/10 to-transparent" />
                            <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.65)]" />
                          </span>
                        )}
                        <Icon size={15} className={`relative ${isActive ? "text-emerald-300" : "text-white/55 group-hover:text-white/85"} transition`} />
                        <span className="relative font-semibold flex-1">{label}</span>
                        <ChevronRight size={12} className={`relative transition ${isActive ? "text-emerald-400 translate-x-0" : "text-white/0 group-hover:text-white/30 -translate-x-1 group-hover:translate-x-0"}`} />
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}

          {/* Operating Handbook (Guideline PDF) */}
          <div className="mt-2 mb-2 rounded-2xl p-3.5 border border-emerald-400/20 relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.10), rgba(245,158,11,0.05))" }}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <BookOpen size={13} className="text-emerald-300" />
                <div className="text-[10px] uppercase tracking-[1.8px] text-emerald-200 font-bold">Operating Handbook</div>
              </div>
              {guidelineMeta?.version && (
                <span className="text-[9px] font-bold text-amber-200 bg-amber-400/15 border border-amber-400/30 rounded px-1.5 py-0.5">
                  {guidelineMeta.version}
                </span>
              )}
            </div>
            <div className="text-[11px] text-white/60 leading-relaxed mb-2.5">
              {guidelineMeta?.description?.split("—")[0]?.trim() || "Complete Agent Panel guideline PDF"}
            </div>
            <button
              onClick={handleGuidelineDownload}
              disabled={downloadingGuide || !guidelineMeta?.available}
              data-testid="agent-guideline-download-btn"
              className="w-full h-9 px-3 inline-flex items-center justify-center gap-1.5 rounded-lg text-[12px] font-bold transition active:scale-95 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloadingGuide ? (
                <><RefreshCw size={12} className="animate-spin" strokeWidth={2.5} /> Preparing…</>
              ) : (
                <><FileText size={12} strokeWidth={2.5} /> Download Guideline PDF</>
              )}
            </button>
            {guidelineMeta?.size > 0 && (
              <div className="mt-1.5 text-[9.5px] text-white/35 text-center font-mono">
                {(guidelineMeta.size / (1024 * 1024)).toFixed(1)} MB · PDF
              </div>
            )}
          </div>

          {/* Get App section */}
          <div className="mt-2 mb-3 rounded-2xl p-3.5 border border-amber-400/20 relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.10), rgba(16,185,129,0.05))" }}>
            <div className="flex items-center gap-2 mb-2">
              <Smartphone size={13} className="text-amber-300" />
              <div className="text-[10px] uppercase tracking-[1.8px] text-amber-200 font-bold">Get the App</div>
            </div>
            <div className="text-[11px] text-white/60 leading-relaxed mb-2.5">Install on your phone for instant notifications & one-tap deposits.</div>
            <div className="space-y-1.5">
              <button
                onClick={handleApkDownload}
                data-testid="agent-apk-download-btn"
                className="w-full h-9 px-3 inline-flex items-center justify-center gap-1.5 rounded-lg text-[12px] font-bold transition active:scale-95 border border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
              >
                <Download size={12} strokeWidth={2.5} /> Download Android APK
              </button>
            </div>
          </div>
        </nav>

        {/* Footer */}
        <div className="px-3 pb-4 pt-2 border-t border-white/[0.06]">
          <button
            onClick={handleLogout}
            data-testid="agent-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[12.5px] font-semibold text-rose-300/90 hover:text-rose-200 bg-rose-500/[0.05] hover:bg-rose-500/[0.12] border border-rose-500/15 hover:border-rose-500/30 transition"
          >
            <LogOut size={14} /> <span className="flex-1 text-left">Sign out</span>
          </button>
          <div className="mt-3 flex items-center gap-1.5 text-[10px] text-white/30">
            <Shield size={10} className="text-emerald-500/70" /> Secure session · Anjouan licensed
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10 lg:ml-0">
        {/* Top bar */}
        <header className="h-14 sm:h-16 flex items-center justify-between gap-2 px-3 sm:px-6 sticky top-0 z-20 border-b border-white/[0.05]"
          style={{ background: "rgba(8,12,22,0.65)", backdropFilter: "blur(18px)" }}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden h-10 w-10 rounded-lg grid place-items-center bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition flex-shrink-0"
              aria-label="Open menu"
              data-testid="agent-drawer-open"
            >
              <Menu size={18} />
            </button>
            <div className="min-w-0 flex-1">
              <div className="hidden sm:flex items-center gap-1.5 text-[10.5px] text-white/40 font-semibold uppercase tracking-[1.5px]">
                <span>Agent</span>
                <ChevronRight size={11} className="text-white/25" />
                <span className="text-emerald-300/90">{currentItem?.label || "Panel"}</span>
              </div>
              <div className="text-[12.5px] sm:text-[14px] text-white/85 sm:mt-0.5 font-bold tracking-tight truncate">
                <span className="hidden sm:inline">{greet(now.getHours())}, </span>
                <span className="text-emerald-300">{agent?.name?.split(" ")[0] || agent?.username}</span>
                {agent?.city && <span className="hidden md:inline ml-2 text-white/40 text-[12px] font-medium">· {agent.city}{agent.state ? ` · ${agent.state}` : ""}</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Notification bell */}
            <NotificationBell />

            {/* Balance pill */}
            <button
              onClick={handleRefresh}
              title="Refresh balance"
              data-testid="agent-balance-pill"
              className="group inline-flex items-center gap-2 h-10 pl-2.5 pr-3 rounded-xl border border-emerald-500/30 hover:border-emerald-400/60 transition relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.06) 100%)" }}
            >
              <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              <div className="relative h-6 w-6 rounded-lg bg-emerald-500/30 grid place-items-center">
                <Wallet size={12} className="text-emerald-200" />
              </div>
              <div className="relative leading-tight text-left">
                <div className="hidden sm:block text-[8.5px] uppercase tracking-[1.5px] text-emerald-300/80 font-bold">Balance</div>
                <div className="font-mono font-extrabold text-[12px] sm:text-[13px] text-white tabular-nums">
                  ৳{Number(agent?.balance || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <RefreshCw size={11} className={`relative text-emerald-300/70 hidden sm:block ${refreshing ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
            </button>

            {/* Profile chip — desktop only */}
            <div className="hidden md:flex h-10 px-2 pr-3 rounded-xl bg-white/[0.04] border border-white/[0.08] items-center gap-2 hover:bg-white/[0.06] transition">
              <div className="h-7 w-7 rounded-lg grid place-items-center font-black text-[11px]"
                style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#1a1208" }}>
                {initials}
              </div>
              <div className="hidden lg:block leading-tight">
                <div className="text-[11.5px] text-white/85 font-bold truncate max-w-[140px]">{agent?.name}</div>
                <div className="text-[9.5px] text-white/40 font-mono">@{agent?.username}</div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-3 sm:px-6 py-4 sm:py-6 overflow-y-auto" data-testid="agent-main" data-scroll-root="true">
          <Outlet />
        </main>
      </div>

      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 6px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 4px; }
        .custom-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.18); }
      `}</style>
    </div>
  );
}
